import { useEffect, useMemo, useState } from 'react';
import {
    Box, Paper, Typography, Button, Skeleton, Stack, Pagination, useMediaQuery, useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api from '../../utils/axios';
import { useCart } from '../../contexts/CartContext';
import { useFavorites } from '../../contexts/FavoritesContext';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage, buildErrorSnackbarOptions } from '../../utils/errorUtils';
import useCatalogueFilters from './useCatalogueFilters';
import CatalogueFilters from './CatalogueFilters';
import CatalogueTable from './CatalogueTable';
import CatalogueMobileList from './CatalogueMobileList';

export default function Catalogue() {
    const { user } = useAuth();
    const { addItem } = useCart();
    const { count: favoritesCount, mutationVersion } = useFavorites();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const {
        state, searchRaw, setSearchRaw,
        setView, setCategoryIds, setInStockOnly, cycleSort, setPage, clearAll,
        queryParams, pageSize,
    } = useCatalogueFilters(user?.customer_id);

    const [products, setProducts] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [qtyDraft, setQtyDraft] = useState({});

    // Fetch categories once (global, not scoped to filters per design doc).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get('/b2b/categories');
                if (!cancelled) setCategories(data.categories || []);
            } catch (err) {
                if (cancelled) return;
                enqueueSnackbar(
                    getErrorMessage(err, 'Impossible de charger les catégories'),
                    buildErrorSnackbarOptions(err, closeSnackbar),
                );
                setCategories([]);
            }
        })();
        return () => { cancelled = true; };
    }, [enqueueSnackbar, closeSnackbar]);

    // Dans l'onglet Favoris, retirer une étoile doit faire disparaître la ligne.
    // On se raccroche à `mutationVersion`, incrémenté quand l'écriture est
    // RETOMBÉE côté serveur — se raccrocher à l'ensemble optimiste ferait
    // partir le GET avant que le DELETE ait abouti, et le produit retiré
    // reviendrait dans la réponse. Hors de cet onglet, aucune étoile ne
    // déclenche de rechargement. Passer par le serveur garde `total` et la
    // pagination justes, ce qu'un filtrage local ne ferait pas.
    const favoritesKey = state.view === 'favorites' ? mutationVersion : 0;

    // Fetch products whenever queryParams change. Keep stale rows visible during
    // refetch instead of flashing skeletons (see CatalogueTable's overlay behavior).
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get('/b2b/products', { params: queryParams });
                if (cancelled) return;
                setProducts(data.products || []);
                setTotal(data.total || 0);
            } catch (err) {
                if (cancelled) return;
                enqueueSnackbar(
                    getErrorMessage(err, 'Chargement du catalogue impossible'),
                    buildErrorSnackbarOptions(err, closeSnackbar),
                );
                setProducts([]);
                setTotal(0);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [queryParams, favoritesKey, enqueueSnackbar, closeSnackbar]);

    const pageCount = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    // La liste peut rétrécir sous nos pieds (favori retiré, produit archivé,
    // filtre resserré). Sans ce recadrage, on reste sur une page qui n'existe
    // plus : la pagination disparaît et l'écran annonce « aucun produit »
    // alors que la liste n'est pas vide.
    useEffect(() => {
        if (!loading && state.page > pageCount) setPage(pageCount);
    }, [loading, state.page, pageCount, setPage]);

    const quantityFor = (p) => qtyDraft[p.id] ?? p.case_pack ?? 1;

    const handleQuantityChange = (productId, value) => {
        setQtyDraft((d) => ({ ...d, [productId]: value }));
    };

    const handleAdd = (p) => {
        const qty = quantityFor(p);
        if (qty <= 0) return;
        addItem(p, qty);
        setQtyDraft((d) => ({ ...d, [p.id]: p.case_pack ?? 1 }));
        enqueueSnackbar(`${p.name} ajouté au panier`, { variant: 'success', autoHideDuration: 2000 });
    };

    const hasFilters = state.search
        || state.view === 'history'
        || state.view === 'favorites'
        || state.categoryIds.length > 0
        || state.inStockOnly;

    // Un onglet Favoris vide n'est pas un filtre trop serré : c'est une liste
    // qu'on n'a pas encore construite. « Effacer les filtres » n'y répond pas,
    // « voir le catalogue » oui.
    const emptyFavorites = state.view === 'favorites'
        && favoritesCount === 0
        && !state.search
        && state.categoryIds.length === 0
        && !state.inStockOnly;
    const isInitialLoad = products === null;

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h1">Catalogue</Typography>
                <Typography variant="body2">Prix négociés appliqués automatiquement.</Typography>
            </Box>

            <CatalogueFilters
                view={state.view}
                onViewChange={setView}
                searchRaw={searchRaw}
                onSearchChange={setSearchRaw}
                categories={categories}
                categoryIds={state.categoryIds}
                onCategoryIdsChange={setCategoryIds}
                inStockOnly={state.inStockOnly}
                onInStockOnlyChange={setInStockOnly}
                favoritesCount={favoritesCount}
            />

            {isInitialLoad ? (
                <Paper sx={{ p: 2 }}>
                    <Stack spacing={1}>
                        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} height={48} variant="rectangular" />)}
                    </Stack>
                </Paper>
            ) : products.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    {emptyFavorites ? (
                        <>
                            <Typography variant="body1">Aucun favori pour le moment.</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Touchez l'étoile d'un produit pour le garder ici. La liste est
                                partagée avec les autres acheteurs de votre entreprise.
                            </Typography>
                            <Button sx={{ mt: 2 }} variant="contained" onClick={() => setView('all')}>
                                Parcourir le catalogue
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="body1">Aucun produit trouvé.</Typography>
                            {hasFilters && (
                                <Button sx={{ mt: 2 }} onClick={clearAll}>
                                    Effacer les filtres
                                </Button>
                            )}
                        </>
                    )}
                </Paper>
            ) : isMobile ? (
                <CatalogueMobileList
                    products={products}
                    loading={loading}
                    quantityFor={quantityFor}
                    onQuantityChange={handleQuantityChange}
                    onAdd={handleAdd}
                />
            ) : (
                <CatalogueTable
                    products={products}
                    loading={loading}
                    sortBy={state.sortBy}
                    sortOrder={state.sortOrder}
                    onSortClick={cycleSort}
                    quantityFor={quantityFor}
                    onQuantityChange={handleQuantityChange}
                    onAdd={handleAdd}
                />
            )}

            {pageCount > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Pagination count={pageCount} page={state.page} onChange={(_, v) => setPage(v)} color="primary" />
                </Box>
            )}
        </Stack>
    );
}
